import { extent } from "d3-array";

import {
  normalizeToZeroDownPayment,
  recalculateForDownPayment,
  recalculateForDownPaymentFast,
} from "pages/Detail/components/Finance/financingoffer";

import {
  TVehicle,
  TFilterFinance,
  TFinance,
  TFinanceCompressed,
  TOffer,
  TOfferCalculated,
  TOfferCompressed,
  TOfferType,
} from "types";

export const getOffersFromVehicle = (
  vehicle: TVehicle,
  excludeBusinessOffers: boolean = true,
): TOfferCalculated[] => {
  const vehicleFinance = vehicle.finance as
    | TFinance[]
    | TFinanceCompressed[]
    | undefined;
  if (!vehicleFinance || vehicleFinance.length === 0) return [];

  const vehicleOffers = vehicleFinance.flatMap(
    ({ type, rates, bank, maintenanceIncluded, wearIncluded }) => {
      const rateOffers = rates.flatMap(({ runtime, offers }) => {
        const normalizedOffers = offers.map(normalizeOffer);
        return normalizedOffers
          .map((normalizedOffer) => {
            const offerId =
              type +
              "-" +
              runtime +
              (normalizedOffer.mileage ? "-" + normalizedOffer.mileage : "");
            const customerType = getCustomerType(type);

            if (excludeBusinessOffers && customerType === "business")
              return undefined;
            const {
              rate: currentRate,
              downPayment,
              netLoanAmount,
            } = normalizedOffer;
            let calculatedOffer;
            if (!netLoanAmount) {
              calculatedOffer = {
                ...normalizedOffer,
                downPayment: 0,
                rate: calculateRateWithoutDownPaymentFast({
                  currentRate,
                  downPayment,
                  durationMonths: runtime,
                }),
              };
            } else {
              calculatedOffer = normalizeToZeroDownPayment(
                normalizedOffer,
                runtime,
              );
            }

            return {
              id: offerId,
              ...calculatedOffer,
              type,
              financeType: normalizeType(type),
              bank,
              runtime,
              originalRate: normalizedOffer.rate,
              maintenanceIncluded,
              wearIncluded,
            } as TOfferCalculated;
          })
          .filter((offer) => !!offer);
      });
      return rateOffers;
    },
  );
  return vehicleOffers;
};

export const getValidOffersFromVehicle = (
  vehicle: TVehicle,
  financeFilter: TFilterFinance,
): TOfferCalculated[] => {
  if (!financeFilter) return [];

  const offers = getOffersFromVehicle(vehicle);

  if (offers.length === 0) return [];
  const {
    rate: financeFilterRate,
    deposit: financeFilterDeposit,
    customerType,
    runTime: financeFilterRuntime,
    mileage: financeFilterMileage,
  } = financeFilter;
  const financeTypes = ["leasing", "balloon", "credit"] as const;
  const validTypes: TOfferType[] = financeTypes.flatMap((financeType) =>
    financeFilter[financeType]
      ? ([financeType, `${financeType}${customerType}`] as TOfferType[])
      : [],
  );
  const validOffers = offers
    .filter(
      ({ type: financeType }) =>
        !financeType || validTypes.includes(financeType),
    )
    .filter(
      ({ runtime }) =>
        !financeFilterRuntime || !runtime || runtime <= financeFilterRuntime,
    )
    .filter(({ mileage: offerMileage }) => {
      if (!offerMileage) return true;
      return !financeFilterMileage || offerMileage >= financeFilterMileage;
    });

  const calculatedOffers = validOffers
    .map((offer) => {
      if (!financeFilterDeposit || !offer.runtime) return offer;
      let calculatedOffer;
      if (!offer.netLoanAmount) {
        calculatedOffer = recalculateForDownPaymentFast(
          offer,
          financeFilterDeposit,
          offer.runtime,
        );
      } else {
        calculatedOffer = recalculateForDownPayment(
          offer,
          financeFilterDeposit,
          offer.runtime,
        );
      }
      return calculatedOffer;
    })
    .filter(({ rate: offerRate }) => {
      if (!financeFilterRate) return true;
      return (
        offerRate >= financeFilterRate[0] && offerRate <= financeFilterRate[1]
      );
    })
    .map((offer) => ({ ...offer, customerType: getCustomerType(offer.type) }));

  return calculatedOffers;
};

export const getAllRates = (vehicle: TVehicle): TOfferCalculated[] => {
  const offers = getOffersFromVehicle(vehicle);

  if (offers.length === 0) return [];

  const calculatedOffers = offers
    .map((offer) => {
      if (!offer.runtime) return offer;
      let calculatedOffer;
      if (!offer.netLoanAmount) {
        calculatedOffer = recalculateForDownPaymentFast(
          offer,
          0,
          offer.runtime,
        );
      } else {
        calculatedOffer = recalculateForDownPayment(offer, 0, offer.runtime);
      }
      return calculatedOffer;
    })
    .map((offer) => ({ ...offer, customerType: getCustomerType(offer.type) }));
  return calculatedOffers;
};

export const getCustomerType = (type: string) => {
  if (type.endsWith("business")) return "business";
  return "private";
};

export const normalizeType = (type: string) => {
  if (type.startsWith("credit")) return "credit";
  if (type.startsWith("balloon")) return "balloon";
  if (type.startsWith("leasing")) return "leasing";
  return type;
};

export const getFinanceRanges = (vehicle: TVehicle, customerType: string) => {
  const ranges = new Map();
  if (!vehicle.finance) return ranges;

  const offers = getOffersFromVehicle(vehicle);
  ["balloon", "leasing", "credit"].forEach((type) => {
    const filtered = offers.filter((offer) =>
      [type, type + customerType].includes(offer.type),
    );
    if (filtered.length > 0) {
      ranges.set(type, {
        runtime: extent(filtered.map((o) => o.runtime)),
        mileage: extent(filtered.map((o) => o.mileage)),
      });
    }
  });

  return ranges;
};

export const getValidPaymentsFromVehicle = (
  vehicle: TVehicle,
  financeFilter: TFilterFinance,
): number[] => {
  if (!financeFilter) return [];

  const {
    type,
    rate: financeFilterRate,
    deposit: financeFilterDeposit,
    customerType,
    runTime: financeFilterRuntime,
    mileage: financeFilterMileage,
  } = financeFilter;

  const financeTypes = ["leasing", "balloon", "credit"] as const;
  const validTypes = financeTypes.flatMap((financeType) =>
    financeFilter[financeType] ? [type, `${type}${customerType}`] : [],
  );

  const minimalRate: number | null = process.env.REACT_APP_MINIMUM_RATE
    ? parseInt(process.env.REACT_APP_MINIMUM_RATE)
    : null;

  const vehicleFinance = vehicle.finance as
    | TFinance[]
    | TFinanceCompressed[]
    | undefined;
  if (!vehicleFinance || vehicleFinance.length === 0) return [];

  const validPrices = vehicleFinance
    .filter(({ type: financeType }) => validTypes.includes(financeType))
    .flatMap(({ type, rates }) => {
      const prices = rates
        .filter(({ runtime }) => runtime <= financeFilterRuntime)
        .flatMap(({ runtime, offers }) => {
          const normalizedOffers = offers.map(normalizeOffer);
          return normalizedOffers
            .filter(
              ({ mileage: offerMileage }) =>
                offerMileage <= financeFilterMileage,
            )
            .map(({ rate: offerPayment }) => {
              const monthlyDeposit = financeFilterDeposit / runtime;
              const calculatedPrice = offerPayment - monthlyDeposit;
              return minimalRate && minimalRate > calculatedPrice
                ? minimalRate
                : calculatedPrice;
            });
        });
      return prices;
    })
    .filter(
      (price) => price >= financeFilterRate[0] && price <= financeFilterRate[1],
    );
  return validPrices;
};

const DEfAULT_APR = 3.99;

const normalizeOffer = (offer: TOffer | TOfferCompressed): TOffer => {
  if (Array.isArray(offer)) {
    if (offer.length === 1) {
      return {
        mileage: 0,
        rate: offer[0],
        apr: DEfAULT_APR,
        downPayment: 0,
      } as TOffer;
    }
    return {
      mileage: offer[0],
      rate: offer[1],
      apr: offer[2] || DEfAULT_APR,
      downPayment: offer[3] || 0,
    } as TOffer;
  }
  return offer;
};

export const removeDeposit = (
  offer: TOfferCompressed,
  runtime: number,
): TOfferCompressed => {
  const normalizedOffer = normalizeOffer(offer);
  const newRate = calculateRateWithoutDownPaymentFast({
    currentRate: normalizedOffer.rate,
    downPayment: normalizedOffer.downPayment,
    durationMonths: runtime,
  });
  return [normalizedOffer.mileage, newRate, normalizedOffer.apr, 0];
};

interface IOffer {
  price: number;
  apr: number;
  downPayment: number;
  runtime: number;
}
export const removeDepositFromPrice = ({
  price,
  apr,
  downPayment,
  runtime,
}: IOffer): number => {
  if (apr && downPayment && downPayment > 0 && price > 0) {
    const monthlyRate = apr / 100 / 12;
    const addedMonthlyCost =
      (downPayment * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -runtime));
    const newPrice = Math.round((price + addedMonthlyCost) * 1000) / 1000;
    return newPrice;
  }
  return price;
};

/**
 * Parameter für die Berechnung einer Schlussratenfinanzierung.
 */
interface FinancingParams {
  vehiclePrice: number; // Der ursprüngliche Fahrzeugpreis (z.B. 15700)
  downPayment: number; // Die geleistete Anzahlung (z.B. 0 bis 8000)
  balloonPayment: number; // Die festgelegte Schlussrate (z.B. 6574.81)
  durationMonths: number; // Laufzeit in Monaten (z.B. 60)
  annualInterestRate: number; // Sollzinssatz p.a. in Prozent (z.B. 5.83)
}

/**
 * Ergebnis der Finanzierungsberechnung.
 * Alle Werte sind auf zwei Nachkommastellen gerundet.
 */
export interface FinancingResult {
  netLoanAmount: number; // Tatsächlich zu finanzierender Betrag
  monthlyRate: number; // Reine Kreditrate pro Monat
  totalInterest: number; // Gesamte Zinskosten über die Laufzeit
  totalPayment: number; // Gesamtsumme aller Zahlungen (Raten + Schlussrate)
  interestSavings: number; // Gesparte Zinsen im Vergleich zu 0€ Anzahlung
}

/**
 * Berechnet die monatliche Rate und die Gesamtkosten einer Schlussratenfinanzierung.
 */
export const calculateFinancing = (
  params: FinancingParams,
): FinancingResult => {
  const {
    vehiclePrice,
    downPayment,
    balloonPayment,
    durationMonths: n,
    annualInterestRate,
  } = params;

  // 1. Nettodarlehensbetrag (Kreditbasis)
  const netLoanAmount = vehiclePrice - downPayment;

  // 2. Monatlicher Zinssatz als Dezimalzahl (i)
  const i = annualInterestRate / 100 / 12;

  // 3. Monatliche Rate berechnen
  let monthlyRate = 0;

  if (i === 0) {
    // Edge Case: 0% Finanzierung
    monthlyRate = (netLoanAmount - balloonPayment) / n;
  } else {
    // Standardformel für Ballonfinanzierung:
    // P = (S0 * (1 + i)^n - R) / (((1 + i)^n - 1) / i)
    const factor = Math.pow(1 + i, n);
    const numerator = netLoanAmount * factor - balloonPayment;
    const denominator = (factor - 1) / i;
    monthlyRate = numerator / denominator;
  }

  // 4. Gesamtzahlung an die Bank (Summe aller Raten + Schlussrate)
  const totalPayment = monthlyRate * n + balloonPayment;

  // 5. Zinskosten
  const totalInterest = totalPayment - netLoanAmount;

  // 6. Zinsersparnis (Vergleichssimulation mit 0€ Anzahlung)
  let interestSavings = 0;
  if (downPayment > 0 && i > 0) {
    const factor = Math.pow(1 + i, n);
    const zeroDownNumerator = vehiclePrice * factor - balloonPayment;
    const zeroDownDenominator = (factor - 1) / i;
    const zeroDownMonthlyRate = zeroDownNumerator / zeroDownDenominator;

    const zeroDownTotalPayment = zeroDownMonthlyRate * n + balloonPayment;
    const zeroDownTotalInterest = zeroDownTotalPayment - vehiclePrice;

    interestSavings = zeroDownTotalInterest - totalInterest;
  }

  // Hilfsfunktion für kaufmännisches Runden auf 2 Nachkommastellen
  const round = (val: number): number => Math.round(val * 100) / 100;

  return {
    netLoanAmount: round(netLoanAmount),
    monthlyRate: round(monthlyRate),
    totalInterest: round(totalInterest),
    totalPayment: round(totalPayment),
    interestSavings: Math.max(0, round(interestSavings)), // Vermeidet negative -0 Ergebnisse
  };
};
/**
 * Parameter für die Berechnung der Rate ohne Anzahlung.
 */
export interface ZeroDownPaymentParams {
  currentRate: number; // Die aktuell ausgewiesene Rate inkl. Services (z.B. 194.882)
  netLoanAmount: number; // Der aktuelle Nettodarlehensbetrag NACH Anzahlung (z.B. 13049.68)
  downPayment: number; // Die aktuell geleistete Anzahlung (z.B. 3730)
  balloonPayment: number; // Die Schlussrate (z.B. 10200)
  borrowingRate: number; // Sollzinssatz p.a. (z.B. 4.88)
  durationMonths: number; // Laufzeit in Monaten (Fehlt im JSON, z.B. 48)
}

/**
 * Berechnet die neue Gesamtrate, wenn die Anzahlung auf 0 gesetzt wird,
 * und behält dabei die ursprüngliche Service-Pauschale bei.
 */
export const calculateRateWithoutDownPayment = (
  params: ZeroDownPaymentParams,
): number => {
  const {
    currentRate,
    netLoanAmount,
    downPayment,
    balloonPayment,
    borrowingRate,
    durationMonths: n,
  } = params;

  // Monatlicher Zinssatz als Dezimalzahl (i)
  const i = borrowingRate / 100 / 12;

  // Hilfsfunktion zur Berechnung der reinen Finanzierungsrate
  const calculatePureRate = (loanAmount: number): number => {
    if (i === 0) {
      return (loanAmount - balloonPayment) / n;
    }
    const factor = Math.pow(1 + i, n);
    const numerator = loanAmount * factor - balloonPayment;
    const denominator = (factor - 1) / i;
    return numerator / denominator;
  };

  // 1. Berechne die REINE Kreditrate im aktuellen Zustand
  const currentPureRate = calculatePureRate(netLoanAmount);

  // 2. Isoliere die versteckte Service-Gebühr (z.B. Garantieverlängerung, Wartung)
  const serviceFee = currentRate - currentPureRate;

  // 3. Berechne den NEUEN Kreditbetrag (ohne Anzahlung)
  // Wenn die Anzahlung auf 0 sinkt, muss dieser Betrag zusätzlich finanziert werden.
  const newNetLoanAmount = netLoanAmount + downPayment;

  // 4. Berechne die NEUE reine Kreditrate
  const newPureRate = calculatePureRate(newNetLoanAmount);

  // 5. Addiere die isolierte Service-Gebühr zur neuen Kreditrate
  const finalNewRate = newPureRate + serviceFee;

  // Kaufmännisches Runden auf 2 Nachkommastellen
  return Math.round(finalNewRate * 100) / 100;
};

/**
 * Parameter für die Berechnung des Ratenaufschlags bei Wegfall der Anzahlung.
 */
export interface RateDeltaParams {
  currentRate: number; // Die aktuelle Rate (z.B. 119)
  downPayment: number; // Die Anzahlung, die wegfallen soll (z.B. 2000)
  apr?: number; // Der Zinssatz p.a. (Optional! z.B. 5.99)
  durationMonths: number; // Die Laufzeit in Monaten (z.B. 48)
}

/**
 * Berechnet die neue Gesamtrate, wenn die Anzahlung auf 0 gesetzt wird.
 * Funktioniert OHNE Kenntnis von Kaufpreis, Restwert oder Schlussrate!
 * Fällt automatisch auf eine Näherungsrechnung zurück, falls 'apr' fehlt.
 */
export const calculateRateWithoutDownPaymentFast = (
  params: RateDeltaParams,
): number => {
  const { currentRate, downPayment, apr = 3.99, durationMonths } = params;

  let additionalMonthlyRate = 0;

  // Wenn kein Zinssatz (apr) übergeben wurde oder er 0 ist,
  // nutzen wir die einfache lineare Näherung.
  if (!apr || apr === 0) {
    additionalMonthlyRate = downPayment / durationMonths;
  } else {
    // Monatlicher Zinssatz als Dezimalzahl (i)
    const i = apr / 100 / 12;
    // Klassische Annuitätenformel für den "Zusatzkredit" (die fehlende Anzahlung)
    // Formel: A = K * (i * (1 + i)^n) / ((1 + i)^n - 1)
    const factor = Math.pow(1 + i, durationMonths);
    additionalMonthlyRate = downPayment * ((i * factor) / (factor - 1));
  }

  // Die neue Rate ist die alte Rate plus der Aufschlag für die finanzierte Anzahlung
  const newRate = currentRate + additionalMonthlyRate;

  // Kaufmännisches Runden auf 2 Nachkommastellen
  return Math.round(newRate * 100) / 100;
};
